// src/features/game/pages/GameDetailsPage.tsx

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  ArrowLeft,
  Award,
  ChevronLeft,
  ChevronRight,
  Info,
  RotateCcw,
  Tv,
} from 'lucide-react';

import axios from 'axios';

import {
  approvePlayer,
  cancelPayment,
  decreaseEarlyChip,
  deletePlayerJoinRequest,
  fetchGameDetails,
  finishGame,
  increaseEarlyChip,
  joinGuest,
  joinPlayer,
  nextLevel,
  pauseGame,
  prevLevel,
  rebuyPlayer,
  removePlayer,
  resumeGame,
  setPrizes,
  startGame,
  updatePayment,
} from '../services/gameService';

import type {
  GameStatus,
  PaymentMethod,
  PaymentRequest,
  Player,
  PlayGame,
  Prize,
} from '../types/game.types';

import {
  memberService,
} from '../../member/services/memberService';

import AddPlayerModal from '../components/AddPlayerModal';
import AddGuestModal from '../components/AddGuestModal';
import PrizesModal from '../components/PrizesModal';
import PaymentModal from '../components/PaymentModal';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import RouteConfig from '../../../routes/RouteConfig';

import {
  formatCurrency,
  formatNumber,
} from '../../../shared/utils/formatUtils';

import {
  calculateElapsedTime,
  formatRemainingTime,
} from '../../../shared/utils/timeUtils';

interface ApiErrorPayload {
  message?: string;
}

const PAYMENT_METHOD_NAMES: Record<
  PaymentMethod,
  string
> = {
  CARD: '카드',
  CASH: '현금',
  TRANSFER: '계좌이체',
  POINT: '라포이트 시드',
  MIXED: '혼합',
};

const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (axios.isAxiosError(error)) {
    const payload =
      error.response?.data as
        | ApiErrorPayload
        | undefined;

    return (
      payload?.message ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return (
      error.message ||
      fallback
    );
  }

  return fallback;
};

const getGameStatusLabel = (
  status: GameStatus,
): string => {
  switch (status) {
    case 'WAIT':
      return '대기';

    case 'RUNNING':
    case 'STARTED':
      return '진행중';

    case 'PAUSED':
      return '일시정지';

    case 'FINISHED':
      return '종료';

    default:
      return status;
  }
};

const getGameStatusClassName = (
  status: GameStatus,
): string => {
  switch (status) {
    case 'WAIT':
      return 'bg-gray-100 text-gray-700';

    case 'RUNNING':
    case 'STARTED':
      return 'bg-green-100 text-green-700';

    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-700';

    case 'FINISHED':
      return 'bg-red-100 text-red-700';

    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const sortPlayers = (
  first: Player,
  second: Player,
): number => {
  if (
    first.status === 'DIE' &&
    second.status !== 'DIE'
  ) {
    return 1;
  }

  if (
    first.status !== 'DIE' &&
    second.status === 'DIE'
  ) {
    return -1;
  }

  return 0;
};

const GameDetailsPage:
  React.FC = () => {
  const {
    playGameId,
  } =
    useParams<{
      playGameId: string;
    }>();

  const navigate =
    useNavigate();

  const modal =
    useModal();

  const [
    game,
    setGame,
  ] =
    useState<PlayGame | null>(
      null,
    );

  const [
    loading,
    setLoading,
  ] =
    useState<boolean>(
      true,
    );

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState<boolean>(
      false,
    );

  const [
    isAddPlayerModalOpen,
    setIsAddPlayerModalOpen,
  ] =
    useState<boolean>(
      false,
    );

  const [
    isGuestModalOpen,
    setIsGuestModalOpen,
  ] =
    useState<boolean>(
      false,
    );

  const [
    isPrizesModalOpen,
    setIsPrizesModalOpen,
  ] =
    useState<boolean>(
      false,
    );

  const [
    isPaymentModalOpen,
    setIsPaymentModalOpen,
  ] =
    useState<boolean>(
      false,
    );

  const [
    selectedPlayerForPayment,
    setSelectedPlayerForPayment,
  ] =
    useState<Player | null>(
      null,
    );

  const [
    ,
    setTick,
  ] =
    useState<number>(
      0,
    );

  const [
    localRemainingSeconds,
    setLocalRemainingSeconds,
  ] =
    useState<number | null>(
      null,
    );

  const [
    manualStatus,
    setManualStatus,
  ] =
    useState<GameStatus | null>(
      null,
    );

  const loadGameDetails =
    useCallback(
      async (): Promise<void> => {
        if (!playGameId) {
          return;
        }

        const parsedPlayGameId =
          Number(
            playGameId,
          );

        if (
          Number.isNaN(
            parsedPlayGameId,
          )
        ) {
          await modal.alert(
            '잘못된 게임 번호입니다.',
          );

          navigate(
            RouteConfig.Management
              .Games.fullPath,
          );

          return;
        }

        try {
          setLoading(
            true,
          );

          const data =
            await fetchGameDetails(
              parsedPlayGameId,
            );

          setGame(
            data,
          );

          const savedStatus =
            localStorage.getItem(
              `game_${playGameId}_status`,
            );

          if (savedStatus) {
            setManualStatus(
              savedStatus as GameStatus,
            );
          } else {
            setManualStatus(
              null,
            );
          }
        } catch (
          error: unknown
        ) {
          console.error(
            '게임 상세 조회 실패:',
            error,
          );

          await modal.alert(
            '게임 정보를 불러오는데 실패했습니다.',
          );

          navigate(
            RouteConfig.Management
              .Games.fullPath,
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        playGameId,
        navigate,
        modal,
      ],
    );

  useEffect(() => {
    void loadGameDetails();
  }, [
    loadGameDetails,
  ]);

  useEffect(() => {
    if (
      game
        ?.levelRemainingSeconds !==
        null &&
      game
        ?.levelRemainingSeconds !==
        undefined
    ) {
      setLocalRemainingSeconds(
        game.levelRemainingSeconds,
      );
    }
  }, [
    game?.levelRemainingSeconds,
  ]);

  useEffect(() => {
    if (!game) {
      return;
    }

    const status =
      manualStatus ??
      game.gameStatus;

    if (
      status === 'WAIT' ||
      status === 'PAUSED' ||
      status === 'FINISHED'
    ) {
      return;
    }

    const interval =
      window.setInterval(
        () => {
          setTick(
            (previous) =>
              previous + 1,
          );

          setLocalRemainingSeconds(
            (previous) => {
              if (
                previous === null
              ) {
                return null;
              }

              return Math.max(
                0,
                previous - 1,
              );
            },
          );
        },
        1000,
      );

    return () => {
      window.clearInterval(
        interval,
      );
    };
  }, [
    game,
    manualStatus,
  ]);

  const handleStartGame =
    async (): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          '게임을 시작하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await startGame(
          game.playGameId,
        );

        const newStatus:
          GameStatus =
          'RUNNING';

        setManualStatus(
          newStatus,
        );

        localStorage.setItem(
          `game_${game.playGameId}_status`,
          newStatus,
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 시작 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '게임 시작에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleResumeGame =
    async (): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          '게임을 재개하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await resumeGame(
          game.playGameId,
        );

        const newStatus:
          GameStatus =
          'RUNNING';

        setManualStatus(
          newStatus,
        );

        localStorage.setItem(
          `game_${game.playGameId}_status`,
          newStatus,
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 재개 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '게임 재개에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handlePauseGame =
    async (): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          '게임을 일시정지하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await pauseGame(
          game.playGameId,
        );

        const newStatus:
          GameStatus =
          'PAUSED';

        setManualStatus(
          newStatus,
        );

        localStorage.setItem(
          `game_${game.playGameId}_status`,
          newStatus,
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 일시정지 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '게임 일시정지에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleFinishGame =
    async (): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          '게임을 종료하시겠습니까? (되돌릴 수 없습니다)',
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await finishGame(
          game.playGameId,
        );

        localStorage.removeItem(
          `game_${game.playGameId}_status`,
        );

        setManualStatus(
          null,
        );

        await modal.alert(
          '게임이 종료되었습니다.',
        );

        navigate(
          RouteConfig.Management
            .Games.fullPath,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 종료 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '게임 종료에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleAddPlayer =
    async (
      userId: number,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await joinPlayer(
          game.playGameId,
          userId,
        );

        await loadGameDetails();

        setIsAddPlayerModalOpen(
          false,
        );

        await modal.alert(
          '플레이어 참가 요청이 추가되었습니다. (대기 상태)',
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '플레이어 추가 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '플레이어 추가에 실패했습니다.',
          ),
        );

        throw error;
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleRemovePlayer =
    async (
      userGameId: number,
      userName: string,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          `${userName}님을 탈락 처리하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await removePlayer(
          game.playGameId,
          userGameId,
        );

        await loadGameDetails();

        await modal.alert(
          `${userName}님이 탈락 처리되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '플레이어 탈락 처리 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '탈락 처리에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleRebuy =
    async (
      userGameId: number,
      userName: string,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          `${userName}님의 리바인을 승인하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await rebuyPlayer(
          game.playGameId,
          userGameId,
        );

        await loadGameDetails();

        await modal.alert(
          `${userName}님의 리바인이 승인되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '리바인에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleSetPrizes =
    async (
      prizes: Prize[],
    ): Promise<void> => {
      if (!game) {
        return;
      }

      try {
        await setPrizes(
          game.playGameId,
          prizes,
        );

        await loadGameDetails();

        setIsPrizesModalOpen(
          false,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '프라이즈 설정 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '프라이즈 설정에 실패했습니다.',
          ),
        );

        throw error;
      }
    };

  const handleJoinGuest =
    async (
      guestName: string,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      const name =
        guestName.trim();

      if (!name) {
        await modal.alert(
          '비회원 이름을 입력해주세요.',
        );

        return;
      }

      try {
        setActionLoading(
          true,
        );

        await joinGuest(
          game.playGameId,
          name,
        );

        await loadGameDetails();

        setIsGuestModalOpen(
          false,
        );

        await modal.alert(
          `비회원 ${name}님이 추가되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '비회원 추가 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '비회원 추가에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const openPaymentModal = (
    player: Player,
  ): void => {
    setSelectedPlayerForPayment(
      player,
    );

    setIsPaymentModalOpen(
      true,
    );
  };

  const handlePayment =
    async (
      paymentMethod:
        PaymentMethod,
      paymentMemo?: string,
    ): Promise<void> => {
      if (
        !game ||
        !selectedPlayerForPayment
      ) {
        return;
      }

      const player =
        selectedPlayerForPayment;

      try {
        setActionLoading(
          true,
        );

        const paymentData:
          PaymentRequest = {
          paymentMethod,
          paymentMemo,
          paid: true,
        };

        await updatePayment(
          player.userGameId,
          paymentData,
        );

        await loadGameDetails();

        setIsPaymentModalOpen(
          false,
        );

        setSelectedPlayerForPayment(
          null,
        );

        await modal.alert(
          `${
            player.name ||
            '플레이어'
          }님의 ${
            PAYMENT_METHOD_NAMES[
              paymentMethod
            ]
          } 결제가 완료되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '결제 처리 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '결제 처리에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleCancelPayment =
    async (
      userGameId: number,
      playerName: string,
    ): Promise<void> => {
      const confirmed =
        await modal.confirm(
          `${playerName}님의 결제를 취소하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await cancelPayment(
          userGameId,
        );

        await loadGameDetails();

        await modal.alert(
          '결제가 취소되었습니다.',
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '결제 취소 실패:',
          error,
        );

        await modal.alert(
          getErrorMessage(
            error,
            '결제 취소에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handlePrevLevel =
    async (): Promise<void> => {
      if (!playGameId) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await prevLevel(
          Number(
            playGameId,
          ),
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '레벨 변경에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleNextLevel =
    async (): Promise<void> => {
      if (!playGameId) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await nextLevel(
          Number(
            playGameId,
          ),
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '레벨 변경에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleIncreaseEarly =
    async (): Promise<void> => {
      if (!playGameId) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await increaseEarlyChip(
          Number(
            playGameId,
          ),
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '얼리칩 증가에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleDecreaseEarly =
    async (): Promise<void> => {
      if (!playGameId) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await decreaseEarlyChip(
          Number(
            playGameId,
          ),
        );

        await loadGameDetails();
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '얼리칩 감소에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleApprovePlayer =
    async (
      userGameId: number,
      userName: string,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await approvePlayer(
          game.playGameId,
          userGameId,
        );

        await loadGameDetails();

        await modal.alert(
          `${userName}님이 승인되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '플레이어 승인에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleDeletePlayer =
    async (
      userGameId: number,
      userName: string,
    ): Promise<void> => {
      if (!game) {
        return;
      }

      const confirmed =
        await modal.confirm(
          `${userName}님의 참가 요청을 삭제하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        await deletePlayerJoinRequest(
          game.playGameId,
          userGameId,
        );

        await loadGameDetails();

        await modal.alert(
          `${userName}님의 참가 요청이 삭제되었습니다.`,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '참가 요청 삭제에 실패했습니다.',
          ),
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  const handleNavigateToMember =
    async (
      userId: number,
    ): Promise<void> => {
      try {
        const member =
          await memberService
            .getMemberById(
              userId,
            );

        navigate(
          RouteConfig.Management
            .MemberDetail.to(
              member.userId,
            ),
          {
            state: {
              from:
                playGameId
                  ? `/management/games/${playGameId}`
                  : RouteConfig
                      .Management
                      .Games
                      .fullPath,
            },
          },
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '회원 조회 실패:',
          error,
        );

        await modal.alert(
          '회원 정보를 찾을 수 없습니다.',
        );
      }
    };

  const calculatePlayerPayment = (
    player: Player,
  ): number => {
    const buyinAmount =
      game?.buyinPrice ??
      0;

    let rebuyAmount =
      0;

    if (
      game?.reEntries &&
      game.reEntries.length >
        0 &&
      player.rebuyinCount >
        0
    ) {
      for (
        let index = 0;
        index <
        player.rebuyinCount;
        index += 1
      ) {
        const reEntry =
          game.reEntries[
            index
          ] ??
          game.reEntries[
            game.reEntries
              .length - 1
          ];

        rebuyAmount +=
          reEntry.price;
      }
    } else {
      rebuyAmount =
        player.rebuyinCount *
        buyinAmount;
    }

    return (
      buyinAmount +
      rebuyAmount
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-20-regular text-gray-600">
          로딩 중...
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-20-regular text-gray-600">
          게임을 찾을 수 없습니다.
        </div>
      </div>
    );
  }

  const gameStatus:
    GameStatus =
    manualStatus ??
    game.gameStatus;

  const isRunning =
    gameStatus ===
      'RUNNING' ||
    gameStatus ===
      'STARTED';

  const isWaiting =
    gameStatus ===
    'WAIT';

  const isFinished =
    gameStatus ===
    'FINISHED';

  const isPaused =
    gameStatus ===
    'PAUSED';

  const sortedPlayers:
    Player[] = [
      ...game.players,
    ].sort(
      sortPlayers,
    );

  const existingPlayerIds:
    number[] =
    game.players.map(
      (
        player: Player,
      ) =>
        player.userId,
    );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={() =>
              navigate(
                RouteConfig.Management
                  .Games.fullPath,
              )
            }
            className="text-gray-600 transition-colors hover:text-gray-900"
            aria-label="게임 목록으로"
          >
            <ArrowLeft
              size={24}
            />
          </button>

          <h1 className="text-32-semibold text-gray-900">
            게임 상세
          </h1>
        </div>

        {/* Game Info */}
        <section className="mb-6 rounded-lg bg-white p-6 shadow-md">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-25-medium text-gray-900">
                {
                  game.gameName
                }

                {game.gameSubtitle
                  ? ` - ${game.gameSubtitle}`
                  : ''}
              </h2>

              <span
                className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getGameStatusClassName(
                  gameStatus,
                )}`}
              >
                {getGameStatusLabel(
                  gameStatus,
                )}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div>
                <div className="text-16-light text-gray-600">
                  참가자
                </div>

                <div className="text-20-semibold text-gray-900">
                  {game.nowPeople}/
                  {game.totalPeople}명
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  시작 스택
                </div>

                <div className="text-20-semibold text-gray-900">
                  {formatNumber(
                    game.startingStack,
                  )}{' '}
                  칩
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  바이인
                </div>

                <div className="text-20-semibold text-gray-900">
                  {formatCurrency(
                    game.buyinPrice,
                  )}
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  총 스택
                </div>

                <div className="text-20-semibold text-gray-900">
                  {formatNumber(
                    game.totalStack,
                  )}{' '}
                  칩
                </div>
              </div>

              {game.startAt && (
                <div>
                  <div className="text-16-light text-gray-600">
                    경과 시간
                  </div>

                  <div className="text-20-semibold text-gray-900">
                    {calculateElapsedTime(
                      game.startAt,
                    )}
                  </div>
                </div>
              )}

              {game.currentLevel !==
                null && (
                <div>
                  <div className="text-16-light text-gray-600">
                    현재 레벨
                  </div>

                  <div className="text-20-semibold text-gray-900">
                    Level{' '}
                    {game.currentLevel}
                  </div>
                </div>
              )}

              {game.currentBlind && (
                <div>
                  <div className="text-16-light text-gray-600">
                    블라인드
                  </div>

                  <div className="text-20-semibold text-gray-900">
                    {
                      game
                        .currentBlind
                        .smallBlind
                    }
                    /
                    {
                      game
                        .currentBlind
                        .bigBlind
                    }

                    {game.nextBlind && (
                      <>
                        {' → '}
                        {
                          game
                            .nextBlind
                            .smallBlind
                        }
                        /
                        {
                          game
                            .nextBlind
                            .bigBlind
                        }
                      </>
                    )}
                  </div>
                </div>
              )}

              {localRemainingSeconds !==
                null && (
                <div>
                  <div className="text-16-light text-gray-600">
                    남은 시간
                  </div>

                  <div className="text-20-semibold text-main">
                    {formatRemainingTime(
                      localRemainingSeconds,
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="text-16-light text-gray-600">
                  평균 스택
                </div>

                <div className="text-20-semibold text-gray-900">
                  {formatNumber(
                    game.averageStack,
                  )}{' '}
                  칩
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  리바인 횟수
                </div>

                <div className="text-20-semibold text-gray-900">
                  {
                    game.rebuyinCount
                  }
                  회
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  바이인 종료 레벨
                </div>

                <div className="text-20-semibold text-gray-900">
                  Level{' '}
                  {
                    game.buyinEndLevel
                  }
                </div>
              </div>

              <div>
                <div className="text-16-light text-gray-600">
                  바이인 제한
                </div>

                <div className="text-20-semibold text-gray-900">
                  {
                    game.buyinLimitCount
                  }
                  회
                </div>
              </div>
            </div>

            {/* Game Controls */}
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
              {isWaiting && (
                <button
                  type="button"
                  onClick={() =>
                    void handleStartGame()
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-blue-500 px-6 py-3 text-16-medium text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {actionLoading
                    ? '처리 중...'
                    : '게임 시작'}
                </button>
              )}

              {isPaused && (
                <button
                  type="button"
                  onClick={() =>
                    void handleResumeGame()
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-green-500 px-6 py-3 text-16-medium text-white transition hover:bg-green-600 disabled:opacity-50"
                >
                  재개
                </button>
              )}

              {isRunning && (
                <button
                  type="button"
                  onClick={() =>
                    void handlePauseGame()
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-yellow-500 px-6 py-3 text-16-medium text-white transition hover:bg-yellow-600 disabled:opacity-50"
                >
                  일시정지
                </button>
              )}

              {!isFinished && (
                <button
                  type="button"
                  onClick={() =>
                    void handleFinishGame()
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-red-500 px-6 py-3 text-16-medium text-white transition hover:bg-red-600 disabled:opacity-50"
                >
                  게임 종료
                </button>
              )}

              {/* TV 화면 */}
              {!isFinished && (
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      RouteConfig.Management
                        .TV.to(
                          game.playGameId,
                        ),
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                  className="flex items-center gap-2 rounded-lg bg-purple-500 px-6 py-3 text-16-medium text-white transition hover:bg-purple-600"
                >
                  <Tv
                    size={20}
                  />

                  TV 화면
                </button>
              )}

              {!isFinished && (
                <button
                  type="button"
                  onClick={() =>
                    setIsPrizesModalOpen(
                      true,
                    )
                  }
                  className="flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-3 text-16-medium text-white transition hover:bg-orange-600"
                >
                  <Award
                    size={20}
                  />

                  프라이즈 설정
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  navigate(
                    RouteConfig.Management
                      .Games.fullPath,
                  )
                }
                className="rounded-lg bg-gray-200 px-6 py-3 text-16-medium text-gray-700 transition hover:bg-gray-300"
              >
                목록으로
              </button>
            </div>
          </div>
        </section>

        {/* Game Operation */}
        {(isRunning ||
          isPaused) && (
          <section className="mb-6 rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-lg font-bold text-gray-900">
              게임 조작
            </h2>

            <div className="flex flex-wrap items-center gap-12">
              <div>
                <div className="mb-2 text-sm text-gray-500">
                  레벨 조작
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      void handlePrevLevel()
                    }
                    disabled={
                      actionLoading ||
                      (game.currentLevel ??
                        0) <= 1
                    }
                    className="flex items-center gap-1 rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
                  >
                    <ChevronLeft
                      size={18}
                    />

                    Prev
                  </button>

                  <div className="rounded-lg bg-main/10 px-6 py-2">
                    <span className="font-bold text-main">
                      Level{' '}
                      {game.currentLevel ??
                        0}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      void handleNextLevel()
                    }
                    disabled={
                      actionLoading
                    }
                    className="flex items-center gap-1 rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
                  >
                    Next

                    <ChevronRight
                      size={18}
                    />
                  </button>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm text-gray-500">
                  얼리칩 조작
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      void handleDecreaseEarly()
                    }
                    disabled={
                      actionLoading ||
                      game.earlyCount <=
                        0
                    }
                    className="rounded-lg bg-gray-100 px-4 py-2 font-bold text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
                  >
                    −
                  </button>

                  <div className="rounded-lg bg-amber-50 px-6 py-2">
                    <span className="font-bold text-amber-700">
                      얼리칩{' '}
                      {game.earlyCount}
                      개
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      void handleIncreaseEarly()
                    }
                    disabled={
                      actionLoading
                    }
                    className="rounded-lg bg-gray-100 px-4 py-2 font-bold text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Player List */}
        <section className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-20-semibold text-gray-900">
              플레이어 목록 (
              {game.players.length}
              명)
            </h2>

            {!isFinished && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setIsAddPlayerModalOpen(
                      true,
                    )
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-main px-4 py-2 text-16-medium text-white transition hover:bg-main-medium disabled:opacity-50"
                >
                  + 플레이어 추가
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setIsGuestModalOpen(
                      true,
                    )
                  }
                  disabled={
                    actionLoading
                  }
                  className="rounded-lg bg-gray-500 px-4 py-2 text-16-medium text-white transition hover:bg-gray-600 disabled:opacity-50"
                >
                  + 비회원 추가
                </button>
              </div>
            )}
          </div>

          {sortedPlayers.length ===
          0 ? (
            <div className="py-12 text-center text-16-regular text-gray-600">
              참가한 플레이어가 없습니다.
            </div>
          ) : (
            <div className="space-y-3">
              {sortedPlayers.map(
                (
                  player: Player,
                ) => (
                  <div
                    key={
                      player.userGameId
                    }
                    className={`rounded-lg border p-4 transition-colors ${
                      player.status ===
                      'DIE'
                        ? player.paymentMethod
                          ? 'border-green-300 bg-green-50 opacity-60'
                          : 'border-gray-300 bg-gray-100 opacity-60'
                        : player.status ===
                            'WAIT'
                          ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                          : player.paymentMethod
                            ? 'border-green-300 bg-green-50 hover:bg-green-100'
                            : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-18-medium text-gray-900">
                            {player.name ||
                              '이름 없음'}

                            {player.nickname
                              ? ` (${player.nickname})`
                              : ''}
                          </span>

                          {!player.guest && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleNavigateToMember(
                                  player.userId,
                                )
                              }
                              className="p-1 text-gray-400 transition hover:text-main"
                              title="회원 상세"
                              aria-label="회원 상세"
                            >
                              <Info
                                size={16}
                              />
                            </button>
                          )}

                          {player.status ===
                            'WAIT' && (
                            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                              대기
                            </span>
                          )}

                          {player.status ===
                            'DIE' && (
                            <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                              탈락
                            </span>
                          )}

                          {player.guest && (
                            <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                              비회원
                            </span>
                          )}

                          {player.early && (
                            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                              얼리칩
                            </span>
                          )}

                          {player.paymentMethod && (
                            <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                              {
                                PAYMENT_METHOD_NAMES[
                                  player.paymentMethod
                                ]
                              }{' '}
                              결제
                            </span>
                          )}
                        </div>

                        {!player.guest && (
                          <div className="mt-1 text-14-regular text-yellow-600">
                            라포이트 시드:{' '}
                            {(
                              player.point ??
                              0
                            ).toLocaleString(
                              'ko-KR',
                            )}{' '}
                            LP
                          </div>
                        )}

                        <div className="mt-1 text-14-regular text-gray-500">
                          리바인{' '}
                          {
                            player.rebuyinCount
                          }
                          회 · 총{' '}
                          {formatCurrency(
                            calculatePlayerPayment(
                              player,
                            ),
                          )}
                        </div>

                        {player.paymentMemo && (
                          <div className="mt-2 rounded border border-yellow-200 bg-yellow-50 p-2 text-sm text-gray-700">
                            메모:{' '}
                            {
                              player.paymentMemo
                            }
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                        {player.status ===
                        'WAIT' ? (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                void handleApprovePlayer(
                                  player.userGameId,
                                  player.name ||
                                    '이름 없음',
                                )
                              }
                              disabled={
                                actionLoading
                              }
                              className="rounded-lg bg-green-500 px-4 py-2 text-white transition hover:bg-green-600 disabled:opacity-50"
                            >
                              승인
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                void handleDeletePlayer(
                                  player.userGameId,
                                  player.name ||
                                    '이름 없음',
                                )
                              }
                              disabled={
                                actionLoading
                              }
                              className="rounded-lg bg-red-500 px-4 py-2 text-white transition hover:bg-red-600 disabled:opacity-50"
                            >
                              삭제
                            </button>
                          </>
                        ) : (
                          <>
                            {!player.paymentMethod ? (
                              <button
                                type="button"
                                onClick={() =>
                                  openPaymentModal(
                                    player,
                                  )
                                }
                                disabled={
                                  actionLoading
                                }
                                className="rounded-lg bg-green-500 px-4 py-2 text-white transition hover:bg-green-600 disabled:opacity-50"
                              >
                                결제
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  void handleCancelPayment(
                                    player.userGameId,
                                    player.name ||
                                      '플레이어',
                                  )
                                }
                                disabled={
                                  actionLoading
                                }
                                className="flex items-center gap-1 rounded-lg bg-gray-500 px-3 py-2 text-white transition hover:bg-gray-600 disabled:opacity-50"
                              >
                                <RotateCcw
                                  size={16}
                                />

                                결제취소
                              </button>
                            )}

                            {!isFinished &&
                              (
                                player.status ===
                                  'ALIVE' ||
                                player.status ===
                                  'DIE'
                              ) && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleRebuy(
                                      player.userGameId,
                                      player.name ||
                                        '이름 없음',
                                    )
                                  }
                                  disabled={
                                    actionLoading
                                  }
                                  className="rounded-lg bg-blue-500 px-4 py-2 text-white transition hover:bg-blue-600 disabled:opacity-50"
                                >
                                  리바인
                                </button>
                              )}

                            {!isFinished &&
                              player.status ===
                                'ALIVE' && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleRemovePlayer(
                                      player.userGameId,
                                      player.name ||
                                        '이름 없음',
                                    )
                                  }
                                  disabled={
                                    actionLoading
                                  }
                                  className="rounded-lg bg-red-500 px-4 py-2 text-white transition hover:bg-red-600 disabled:opacity-50"
                                >
                                  탈락
                                </button>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
        </section>
      </div>

      <AddPlayerModal
        isOpen={
          isAddPlayerModalOpen
        }
        onClose={() =>
          setIsAddPlayerModalOpen(
            false,
          )
        }
        onAddPlayer={
          handleAddPlayer
        }
        existingPlayerIds={
          existingPlayerIds
        }
      />

      <PrizesModal
        isOpen={
          isPrizesModalOpen
        }
        onClose={() =>
          setIsPrizesModalOpen(
            false,
          )
        }
        onSubmit={
          handleSetPrizes
        }
      />

      {selectedPlayerForPayment && (
        <PaymentModal
          isOpen={
            isPaymentModalOpen
          }
          onClose={() => {
            setIsPaymentModalOpen(
              false,
            );

            setSelectedPlayerForPayment(
              null,
            );
          }}
          onSubmit={
            handlePayment
          }
          playerName={
            selectedPlayerForPayment.name ||
            '이름 없음'
          }
          paymentAmount={
            calculatePlayerPayment(
              selectedPlayerForPayment,
            )
          }
        />
      )}

      <AddGuestModal
        isOpen={
          isGuestModalOpen
        }
        onClose={() =>
          setIsGuestModalOpen(
            false,
          )
        }
        onSubmit={
          handleJoinGuest
        }
      />
    </div>
  );
};

export default GameDetailsPage;